/* eslint-disable @next/next/no-img-element -- favicon/external/data-URI; next/image migration impractical for these dynamic logo/signature sources */

/**
 * Banner Component
 *
 * The shared action / resolution / review callout primitive: a white surface with
 * colour confined to a single 24px icon chip on a semantic `tone` ladder
 * (neutral / caution / critical / success / ai). One anatomy, slots switch on/off:
 * icon chip · title · optional supporting line · optional action / dismiss · optional
 * per-item list.
 *
 * Migrating a call site is props, not markup — pick a `tone`, an optional `icon`
 * override, a `line`, and up to two `action`s. (The deprecated `severity` / `message`
 * / `link*` back-compat aliases were removed in FE-8; every caller now uses the tone API.)
 */

'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Info,
  X,
  type LucideIcon
} from 'lucide-react';
import { ValeAIMarkIcon } from '@/components/icons/vale-ai-mark';
import { cn } from '@/lib/utils';

export type BannerTone = 'neutral' | 'caution' | 'critical' | 'success' | 'ai';

export interface BannerAction {
  label: string;
  onClick?: () => void;
  /** next/link via asChild (existing behaviour) */
  href?: string;
  /**
   * 'outline' default; 'dark' = the single primary next-step; 'secondary' =
   * filled but not the page's primary; 'link' = the lightest affordance, for a
   * banner whose whole card is already the click target.
   *
   * Two notes from measuring these on a real banner (VALE-1034): `secondary` is
   * barely a step up from `outline` under the current theme — `bg-secondary`
   * resolves to lab(96.5), near white. And `ghost` is the wrong light weight on
   * a clickable card, because its `hover:bg-accent` paints a second hover box
   * inside the one the card is already painting; `link` has no background hover
   * and is what that case wants.
   */
  variant?: 'outline' | 'dark' | 'secondary' | 'ghost' | 'link';
  /** optional trailing icon (e.g. ArrowRight on "Review") */
  icon?: LucideIcon;
  /** open in a new tab with an ExternalLink affordance */
  external?: boolean;
  /** render a plain `<a href download>` (file download; skips next/link + new tab) */
  download?: boolean;
  /** disable the control (e.g. while a mutation is pending) */
  disabled?: boolean;
}

export interface BannerItem {
  id: string | number;
  title: string;
  /** 0–2 muted sub-lines */
  subtitle?: string;
  /** right-aligned, tabular-nums (currency) */
  value?: string;
  /** per-row action ("Decide") */
  action?: BannerAction;
}

export interface BannerProps {
  /** semantic tone — tints only the icon chip. Default 'neutral'. */
  tone?: BannerTone;
  /** overrides the tone default icon (Users, MessageSquare, FileText…) */
  icon?: LucideIcon;
  /** image chip (existing — bank favicon etc.); takes precedence over the tone chip */
  iconUrl?: string;
  /**
   * Caller content in the chip's place, such as a video thumbnail (VALE-2066).
   * Takes precedence over `iconUrl` and the tone chip.
   */
  leading?: React.ReactNode;
  title: React.ReactNode;
  /** optional single supporting line under the title */
  line?: React.ReactNode;
  action?: BannerAction;
  secondaryAction?: BannerAction;
  /** optional per-item list slot ("one row per affected thing") */
  items?: BannerItem[];
  onDismiss?: () => void;
  className?: string;
  /** escape hatch (existing) */
  children?: React.ReactNode;
}

/** Tone chips — colour driven by the --banner-* tokens (see design-tokens-theme.css). */
const TONE_CHIP: Record<BannerTone, string> = {
  neutral:
    'bg-[color:var(--banner-neutral-bg)] text-[color:var(--banner-neutral-fg)]',
  caution:
    'bg-[color:var(--banner-caution-bg)] text-[color:var(--banner-caution-fg)]',
  critical:
    'bg-[color:var(--banner-critical-bg)] text-[color:var(--banner-critical-fg)]',
  success:
    'bg-[color:var(--banner-success-bg)] text-[color:var(--banner-success-fg)]',
  ai: 'bg-[color:var(--banner-ai-bg)] text-[color:var(--banner-ai-fg)]'
};

const TONE_ICON: Record<BannerTone, LucideIcon> = {
  neutral: Info,
  caution: AlertTriangle,
  critical: AlertCircle,
  success: CheckCircle2,
  ai: ValeAIMarkIcon
};

// Padding scaled by the same 24/30 factor the chip was resized by (LF-5587: 4px
// -> 3.2px), so the glyph keeps its original ~73% fill of the chip instead of
// looking more inset now that the box shrank around an unscaled 4px padding.
const CHIP_WRAPPER =
  'flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-lg p-[3.2px]';

function ActionButton({ action }: { action: BannerAction }) {
  const {
    label,
    onClick,
    href,
    variant,
    icon: Icon,
    external,
    download,
    disabled
  } = action;
  const inner = (
    <>
      {label}
      {Icon != null && <Icon size={14} aria-hidden="true" />}
      {external === true && <ExternalLink size={14} aria-hidden="true" />}
    </>
  );

  return (
    <Button
      size="xs"
      density="compact"
      gap="standard"
      type="button"
      variant={
        variant === 'dark'
          ? 'default'
          : variant === 'secondary'
            ? 'secondary'
            : variant === 'ghost'
              ? 'ghost'
              : variant === 'link'
                ? 'link'
                : 'outline'
      }
      className="shrink-0"
      onClick={onClick}
      disabled={disabled}
      asChild={href != null}
    >
      {href != null ? (
        download === true ? (
          <a href={href} download>
            {inner}
          </a>
        ) : external === true ? (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {inner}
          </a>
        ) : (
          <Link href={href}>{inner}</Link>
        )
      ) : (
        inner
      )}
    </Button>
  );
}

export const Banner = ({
  ref,
  tone = 'neutral',
  icon,
  iconUrl = '',
  leading,
  title,
  line,
  action,
  secondaryAction,
  items,
  onDismiss,
  className,
  children
}: BannerProps & {
  ref?: React.RefObject<HTMLDivElement>;
}) => {
  const ChipIcon = icon ?? TONE_ICON[tone];

  /**
   * The Vale mark never sits on a tinted chip (VALE-1251), whichever tone put
   * it there. Every other tone icon is a lucide STROKE drawing that needs a
   * ground to register against; the mark is three filled shapes that carry
   * their own weight, and the tint read as a container around a logo rather
   * than as a tone chip.
   *
   * Keyed on the ICON rather than on the tone, because either can put the mark
   * in the chip: `ai` takes it as its default, and a `neutral` banner passes it
   * explicitly to say "Vale did this" without claiming Vale AI did (the
   * intestacy prefill is the first of those). Keying on `tone === 'ai'` instead
   * would strip the wash from an `ai` banner that overrode the icon, and leave
   * it on every mark that arrived through a different tone.
   *
   * Only the background is dropped; the tone keeps its foreground, so the mark
   * still takes that tone's ink.
   */
  const chipClass = cn(
    TONE_CHIP[tone],
    // ...and it always draws in THE AI purple, `--color-ai`, never the tone's
    // own foreground. VALE-1251 settled on one purple for the mark across the
    // product (the value the redesigned analysing-documents surface already
    // ships, which is `ValeAILogo`'s own default), and `--banner-ai-fg` is a
    // different, less chromatic violet. Measured 5.88:1 on the card in light
    // and comfortably past the 4.5:1 text floor in dark.
    ChipIcon === ValeAIMarkIcon && 'bg-transparent text-ai'
  );

  const hasItems = items != null && items.length > 0;

  return (
    <div
      ref={ref}
      className={cn('border-border bg-card rounded-xl border p-3', className)}
    >
      <div className="grid grid-cols-[auto_1fr] items-center gap-x-3">
        {/* Icon chip */}
        {leading != null ? (
          leading
        ) : iconUrl ? (
          <div className="rounded-lg">
            <img src={iconUrl} width={30} height={30} alt="" />
          </div>
        ) : (
          <div
            data-testid="banner-icon-chip"
            className={cn(CHIP_WRAPPER, chipClass)}
          >
            <ChipIcon className="h-full w-full" />
          </div>
        )}

        {/* Title / line + actions row — shares grid col 2 with children below */}
        <div className="flex w-full min-w-0 items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h3 className="text-foreground text-xs font-semibold tracking-[-0.24px]">
              {title}
            </h3>
            {line != null && (
              <p className="text-muted-foreground text-xs font-medium tracking-[-0.24px]">
                {line}
              </p>
            )}
          </div>

          {/* Primary action */}
          {action != null && <ActionButton action={action} />}

          {/* Secondary action */}
          {secondaryAction != null && <ActionButton action={secondaryAction} />}

          {/* Dismiss */}
          {onDismiss != null && (
            <Button
              tone="muted"
              type="button"
              variant="ghost"
              size="icon-xs"
              className="shrink-0"
              onClick={onDismiss}
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          )}
        </div>

        {/* Escape-hatch content — pinned to col 2 so it shares the title/line
            text column instead of starting under the icon (LF-6034).
            `min-w-0` is load-bearing (VALE-666): without it this grid item's
            automatic minimum is its min-content, so a `truncate`d child (which
            is `white-space: nowrap`) contributes its ENTIRE single-line width
            and stretches the `1fr` track past the card — the truncation never
            fires, and the title/line row above is dragged wide with it so its
            text stops wrapping. Measured at 1706px inside a 727px card. */}
        {children != null && (
          <div className="col-start-2 w-full min-w-0">{children}</div>
        )}
      </div>

      {/* Per-item list slot */}
      {hasItems && (
        <ul className="mt-3 flex flex-col gap-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="border-border flex items-center justify-between gap-3 rounded-lg border p-3"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="text-foreground truncate text-xs font-semibold tracking-[-0.24px]">
                  {item.title}
                </p>
                {item.subtitle != null && (
                  <p className="text-muted-foreground text-xs font-medium tracking-[-0.24px]">
                    {item.subtitle}
                  </p>
                )}
              </div>
              {item.value != null && (
                <span className="text-foreground shrink-0 text-xs font-semibold tracking-[-0.24px] tabular-nums">
                  {item.value}
                </span>
              )}
              {item.action != null && <ActionButton action={item.action} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

Banner.displayName = 'Banner';
